import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { toast } from "react-toastify";
import { chatai } from "../../Context/api";
import { commonMessage } from "../../Constants/generalConfig";

const ChatAi = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { sender: "ai", text: "👋 Hi there! I’m your Iskedo Assistant. How can I help you today?" },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    // ** DRAGGABLE FUNCTIONALITY - State to track button position **
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false); // ** Track if button actually moved during drag **
    const buttonRef = useRef(null);

 // ** DRAGGABLE FUNCTIONALITY - Initialize position on bottom-right **
    useEffect(() => {
        const updatePosition = () => {
            setPosition({
                x: window.innerWidth - 180, // 180px from right edge
                y: window.innerHeight - 80   // 80px from bottom edge
            });
        };
        
        updatePosition();
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
    }, []);

    // ** DRAGGABLE FUNCTIONALITY - Handle mouse down to start dragging **
    const handleMouseDown = (e) => {
        if (isOpen) return; // Don't allow dragging when chat is open
        
        setIsDragging(true);
        setHasMoved(false); // ** Reset moved flag when starting drag **
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;

            // ** Mark that the button has moved **
            setHasMoved(true);

            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            // ** Constrain position within viewport **
            const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 150);
            const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 60);

            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);
    

    const chatBodyRef = useRef(null);

    // ✅ Scroll to bottom automatically when messages update
    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { sender: "user", text: input };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setLoading(true);

        try {
            const payload = { prompt: input };
            const response = await axios.post(chatai(), payload);
            const aiReply = response?.data?.reply || "⚠️ Sorry, I couldn’t process your request.";
            const aiMessage = { sender: "ai", text: aiReply };

            setMessages((prev) => [...prev, aiMessage]);
        } catch (err) {
            toast.error(commonMessage.networkError || "Network Error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="chat-ai-wrapper">
            {!isOpen ? (
                <div ref={buttonRef} className="chat-collapsed"  onClick={() => !hasMoved && setIsOpen(true)} onMouseDown={handleMouseDown}     style={{
                            position: 'fixed',
                            left: `${position.x}px`,
                            top: `${position.y}px`,
                            cursor: isDragging ? 'grabbing' : 'grab',
                            userSelect: 'none',
                            zIndex: 9999
                        }}>
                    {/* <button className="assist-btn">
                        <span className="assist-icon">✨</span>
                        Chat with Iskedo Assistant
                    </button> */}
                    <button className="assist-btn">
                      <span className="assist-icon">✨</span>
                      <span className="assist-text">Chat</span>
                    </button>
                </div>
            ) : (
                <div className="chat-expanded shadow">
                    <div className="chat-header bg-dark text-light">
                        <strong>Iskedo AI Assistant</strong>
                        <Button
                            variant="outline-light"
                            size="sm"
                            onClick={() => setIsOpen(false)}
                        >
                            ✕
                        </Button>
                    </div>

                    {/* ✅ Chat messages section */}
                    <div className="chat-body" ref={chatBodyRef}>
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`message-ai ${msg.sender === "user" ? "user-msg" : "ai-msg"}`}
                            >
                                {msg.sender === "ai" ? (
                                    <ReactMarkdown
                                        components={{
                                            a: ({ node, ...props }) => (
                                                <a
                                                    {...props}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: "#00cc66" }}
                                                />
                                            ),
                                        }}
                                    >{msg.text}</ReactMarkdown>
                                ) : (
                                    <div>{msg.text}</div>
                                )}
                            </div>
                        ))}

                        {loading && (
                            <div className="message-ai ai-msg">
                                <Spinner animation="border" size="sm" />{" "}
                                <span className="ms-2">Thinking...</span>
                            </div>
                        )}
                    </div>

                    <div className="chat-input d-flex align-items-center">
                        <Form.Control
                            type="text"
                            placeholder="Type your message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSend()}
                        />
                        <Button
                            variant="success"
                            className="send-btn"
                            onClick={handleSend}
                        >
                            ➤
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatAi;
